import React, { useEffect, useState } from "react";
import {
    Button, Card, CardActions, CardContent, CardHeader, Checkbox, createStyles, Dialog, DialogActions,
    DialogContent, DialogTitle, FormControlLabel, Grid, IconButton, LinearProgress, MenuItem, Paper,
    Select, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Theme,
    Typography, WithStyles, withStyles
} from "@material-ui/core";
import EditIcon from "@material-ui/icons/Edit";
import DeleteIcon from "@material-ui/icons/Delete";
import FileCopyIcon from "@material-ui/icons/FileCopyOutlined";
import { Counter, CounterSetupRow } from "../../model/Counter";
import { ApiBackend } from "../../providers/apibackend";

const ManageCounters = (props: WithStyles) => {
    const backend = new ApiBackend();

    const [loading, setLoading] = useState(true);
    const [counters, setCounters] = useState<Counter[]>([]);
    const [editCounter, setEditCounter] = useState<Counter | null>(null);
    const [isNewCounter, setIsNewCounter] = useState(false);
    const [confirmDeleteCounter, setConfirmDeleteCounter] = useState<Counter | null>(null);

    const [years, setYears] = useState<number[]>([]);
    const [selectedYear, setSelectedYear] = useState<number | null>(null);
    const [setup, setSetup] = useState<CounterSetupRow[]>([]);
    const [addSetupOpen, setAddSetupOpen] = useState(false);
    const [copyOpen, setCopyOpen] = useState(false);

    useEffect(() => {
        init();
    }, []);

    const init = async () => {
        setLoading(true);
        const c = await backend.listCounters();
        const s = await backend.getCounterSetup();
        setCounters(c);
        const yrs = s.years;
        setYears(yrs);
        const initialYear = yrs.length ? yrs[yrs.length - 1] : new Date().getFullYear();
        setSelectedYear(initialYear);
        const forYear = await backend.getCounterSetup(initialYear);
        setSetup(forYear.setup);
        setYears(forYear.years);
        setLoading(false);
    };

    const fetchCounters = async () => {
        setLoading(true);
        setCounters(await backend.listCounters());
        setLoading(false);
    };

    const fetchSetup = async (year: number) => {
        setLoading(true);
        const res = await backend.getCounterSetup(year);
        setSetup(res.setup);
        setYears(res.years);
        setLoading(false);
    };

    const handleYearChange = (year: number) => {
        setSelectedYear(year);
        fetchSetup(year);
    };

    const handleDeleteCounterOk = async () => {
        setLoading(true);
        await backend.deleteCounter(confirmDeleteCounter.counter_key);
        setConfirmDeleteCounter(null);
        await fetchCounters();
    };

    const toggleAsTeamMember = async (row: CounterSetupRow) => {
        setLoading(true);
        const ok = await backend.addOrUpdateCounterSetup({ ...row, asTeamMember: !row.asTeamMember });
        if (!ok) { alert("Kunde inte spara ändringen"); }
        await fetchSetup(selectedYear);
    };

    const handleDeleteSetup = async (row: CounterSetupRow) => {
        if (!window.confirm(`Ta bort ${row.counter_key} / ${row.eventKey}?`)) { return; }
        setLoading(true);
        await backend.deleteCounterSetup(row.counter_key, row.year, row.eventKey);
        await fetchSetup(selectedYear);
    };

    const render = () => {
        const { classes } = props;
        return (<>
            {loading && <LinearProgress color="secondary" />}

            {/* ---------- Counters ---------- */}
            <Grid container className={classes.root} spacing={2}>
                <Grid item xs={12}>
                    <Card>
                        <CardHeader className={classes.cardHeader} title="Counters" />
                        <CardContent>
                            <TableContainer component={Paper}>
                                <Table size="small" aria-label="counters">
                                    <TableHead>
                                        <TableRow>
                                            <StyledTableCell>Nyckel</StyledTableCell>
                                            <StyledTableCell>Namn</StyledTableCell>
                                            <StyledTableCell>Kategori</StyledTableCell>
                                            <StyledTableCell>Visa på utskrift</StyledTableCell>
                                            <StyledTableCell style={{ width: 90 }}></StyledTableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {counters.map((item) =>
                                            <TableRow key={item.counter_key}>
                                                <StyledTableCell>{item.counter_key}</StyledTableCell>
                                                <StyledTableCell>{item.name}</StyledTableCell>
                                                <StyledTableCell>{item.category}</StyledTableCell>
                                                <StyledTableCell>{item.showOnPrint ? "JA" : "NEJ"}</StyledTableCell>
                                                <StyledTableCell align="right">
                                                    <IconButton size="small" disabled={loading}
                                                        onClick={() => { setIsNewCounter(false); setEditCounter({ ...item }); }}>
                                                        <EditIcon fontSize="small" />
                                                    </IconButton>
                                                    <IconButton size="small" disabled={loading}
                                                        onClick={() => setConfirmDeleteCounter(item)}>
                                                        <DeleteIcon fontSize="small" />
                                                    </IconButton>
                                                </StyledTableCell>
                                            </TableRow>
                                        )}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </CardContent>
                        <CardActions>
                            <Button size="small" color="secondary" disabled={loading}
                                onClick={() => { setIsNewCounter(true); setEditCounter({ counter_key: "", name: "", category: "", showOnPrint: 1 }); }}>
                                Lägg till counter
                            </Button>
                        </CardActions>
                    </Card>
                </Grid>

                {/* ---------- CounterSetup ---------- */}
                <Grid item xs={12}>
                    <Card>
                        <CardHeader className={classes.cardHeader} title="Counter setup (per år)" />
                        <CardContent>
                            <Grid container spacing={2} alignItems="center" style={{ marginBottom: 8 }}>
                                <Grid item>
                                    <Typography variant="body2" component="span" style={{ marginRight: 8 }}>År:</Typography>
                                    <Select native value={selectedYear ?? ""}
                                        onChange={(e) => handleYearChange(+(e.target.value as string))}>
                                        {years.map(y => <option key={y} value={y}>{y}</option>)}
                                        {selectedYear != null && years.indexOf(selectedYear) < 0 &&
                                            <option value={selectedYear}>{selectedYear}</option>}
                                    </Select>
                                </Grid>
                                <Grid item>
                                    <Button size="small" variant="outlined" startIcon={<FileCopyIcon />}
                                        disabled={loading} onClick={() => setCopyOpen(true)}>
                                        Kopiera år som mall
                                    </Button>
                                </Grid>
                            </Grid>
                            <TableContainer component={Paper}>
                                <Table size="small" aria-label="counter setup">
                                    <TableHead>
                                        <TableRow>
                                            <StyledTableCell>Counter</StyledTableCell>
                                            <StyledTableCell>EventKey</StyledTableCell>
                                            <StyledTableCell>Som lagmedlem</StyledTableCell>
                                            <StyledTableCell style={{ width: 60 }}></StyledTableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {setup.map((row) =>
                                            <TableRow key={`${row.counter_key}|${row.eventKey}`}>
                                                <StyledTableCell>{row.counter_key}</StyledTableCell>
                                                <StyledTableCell>{row.eventKey}</StyledTableCell>
                                                <StyledTableCell>
                                                    <Checkbox color="primary" checked={row.asTeamMember} disabled={loading}
                                                        onChange={() => toggleAsTeamMember(row)} />
                                                </StyledTableCell>
                                                <StyledTableCell align="right">
                                                    <IconButton size="small" disabled={loading}
                                                        onClick={() => handleDeleteSetup(row)}>
                                                        <DeleteIcon fontSize="small" />
                                                    </IconButton>
                                                </StyledTableCell>
                                            </TableRow>
                                        )}
                                        {!loading && setup.length === 0 &&
                                            <TableRow><StyledTableCell colSpan={4}>Inga rader för {selectedYear}</StyledTableCell></TableRow>}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </CardContent>
                        <CardActions>
                            <Button size="small" color="secondary" disabled={loading || selectedYear == null}
                                onClick={() => setAddSetupOpen(true)}>
                                Lägg till rad
                            </Button>
                        </CardActions>
                    </Card>
                </Grid>
            </Grid>

            {editCounter &&
                <EditCounterDialog editItem={editCounter} isNew={isNewCounter} existingKeys={counters.map(c => c.counter_key)}
                    onAbort={() => setEditCounter(null)}
                    onSave={async () => { setEditCounter(null); await fetchCounters(); }} />}

            {confirmDeleteCounter &&
                <Dialog disableEscapeKeyDown maxWidth="xs" open={true}>
                    <DialogTitle>Ta bort counter {confirmDeleteCounter.counter_key}?</DialogTitle>
                    <DialogActions>
                        <Button variant="contained" color="primary" onClick={() => setConfirmDeleteCounter(null)}>Avbryt</Button>
                        <Button variant="contained" color="primary" onClick={handleDeleteCounterOk}>Ja, ta bort</Button>
                    </DialogActions>
                </Dialog>}

            {addSetupOpen && selectedYear != null &&
                <AddSetupDialog year={selectedYear} counters={counters} existing={setup}
                    onAbort={() => setAddSetupOpen(false)}
                    onSave={async () => { setAddSetupOpen(false); await fetchSetup(selectedYear); }} />}

            {copyOpen &&
                <CopyYearDialog years={years} defaultSource={selectedYear}
                    onAbort={() => setCopyOpen(false)}
                    onCopied={async (targetYear) => { setCopyOpen(false); setSelectedYear(targetYear); await fetchSetup(targetYear); }} />}
        </>);
    };

    return render();
};

/* ------------------------------------------------------------------ */

type EditCounterDialogProps = {
    editItem: Counter;
    isNew: boolean;
    existingKeys: string[];
    onAbort: () => void;
    onSave: () => void;
};

const EditCounterDialog = (props: EditCounterDialogProps) => {
    const backend = new ApiBackend();
    const [counterKey, setCounterKey] = useState(props.editItem.counter_key);
    const [name, setName] = useState(props.editItem.name ?? "");
    const [category, setCategory] = useState(props.editItem.category ?? "");
    const [showOnPrint, setShowOnPrint] = useState<boolean>(!!props.editItem.showOnPrint);
    const [loading, setLoading] = useState(false);

    const handleSave = async () => {
        const key = (counterKey || "").trim();
        if (props.isNew && props.existingKeys.some(k => k.toLowerCase() === key.toLowerCase())) {
            alert("Denna nyckel finns redan");
            return;
        }
        setLoading(true);
        const ok = await backend.addOrUpdateCounter({ counter_key: key, name, category, showOnPrint: showOnPrint ? 1 : 0 });
        setLoading(false);
        if (ok) { props.onSave(); } else { alert("Kunde inte spara counter"); }
    };

    const canSave = !!(counterKey && counterKey.trim());

    return (
        <Dialog disableEscapeKeyDown fullWidth maxWidth="sm" open={true}>
            <DialogTitle>{props.isNew ? "Lägg till counter" : `Redigera ${props.editItem.counter_key}`}</DialogTitle>
            <DialogContent dividers>
                <Grid container spacing={2}>
                    <Grid item xs={12}>
                        <TextField fullWidth label="Nyckel (counter_key)" InputLabelProps={{ shrink: true }}
                            value={counterKey ?? ""} disabled={!props.isNew}
                            inputProps={{ maxLength: 10 }}
                            onChange={(e) => setCounterKey(e.target.value)} />
                    </Grid>
                    <Grid item xs={12}>
                        <TextField fullWidth label="Namn" InputLabelProps={{ shrink: true }}
                            value={name ?? ""} onChange={(e) => setName(e.target.value)} />
                    </Grid>
                    <Grid item xs={12}>
                        <TextField fullWidth label="Kategori" InputLabelProps={{ shrink: true }}
                            value={category ?? ""} onChange={(e) => setCategory(e.target.value)} />
                    </Grid>
                    <Grid item xs={12}>
                        <FormControlLabel control={<Checkbox color="primary" checked={showOnPrint}
                            onChange={(e) => setShowOnPrint(e.target.checked)} />} label="Visa på utskrift" />
                    </Grid>
                </Grid>
            </DialogContent>
            <DialogActions>
                <Button color="secondary" variant="contained" disabled={loading || !canSave} onClick={handleSave}>Spara</Button>
                <Button color="secondary" variant="contained" style={{ marginLeft: 10 }} disabled={loading} onClick={props.onAbort}>Avbryt</Button>
            </DialogActions>
        </Dialog>
    );
};

/* ------------------------------------------------------------------ */

type AddSetupDialogProps = {
    year: number;
    counters: Counter[];
    existing: CounterSetupRow[];
    onAbort: () => void;
    onSave: () => void;
};

const AddSetupDialog = (props: AddSetupDialogProps) => {
    const backend = new ApiBackend();
    const [counterKey, setCounterKey] = useState<string>(props.counters.length ? props.counters[0].counter_key : "");
    const [eventKey, setEventKey] = useState("");
    const [asTeamMember, setAsTeamMember] = useState(false);
    const [loading, setLoading] = useState(false);

    const handleSave = async () => {
        const ev = (eventKey || "").trim();
        if (!counterKey || !ev) { return; }
        if (props.existing.some(r => r.counter_key === counterKey && r.eventKey.toLowerCase() === ev.toLowerCase())) {
            alert("Raden finns redan för detta år");
            return;
        }
        setLoading(true);
        const ok = await backend.addOrUpdateCounterSetup({ counter_key: counterKey, year: props.year, eventKey: ev, asTeamMember });
        setLoading(false);
        if (ok) { props.onSave(); } else { alert("Kunde inte spara raden"); }
    };

    return (
        <Dialog disableEscapeKeyDown fullWidth maxWidth="sm" open={true}>
            <DialogTitle>Lägg till rad ({props.year})</DialogTitle>
            <DialogContent dividers>
                <Grid container spacing={2}>
                    <Grid item xs={12}>
                        <TextField select fullWidth label="Counter" InputLabelProps={{ shrink: true }}
                            value={counterKey} onChange={(e) => setCounterKey(e.target.value)}>
                            {props.counters.map(c =>
                                <MenuItem key={c.counter_key} value={c.counter_key}>{c.counter_key} — {c.name}</MenuItem>)}
                        </TextField>
                    </Grid>
                    <Grid item xs={12}>
                        <TextField fullWidth label="EventKey (t.ex. CV30-2027)" InputLabelProps={{ shrink: true }}
                            value={eventKey} inputProps={{ maxLength: 10 }} onChange={(e) => setEventKey(e.target.value)} />
                    </Grid>
                    <Grid item xs={12}>
                        <FormControlLabel control={<Checkbox color="primary" checked={asTeamMember}
                            onChange={(e) => setAsTeamMember(e.target.checked)} />} label="Räknas som lagmedlem" />
                    </Grid>
                </Grid>
            </DialogContent>
            <DialogActions>
                <Button color="secondary" variant="contained" disabled={loading || !counterKey || !eventKey.trim()} onClick={handleSave}>Spara</Button>
                <Button color="secondary" variant="contained" style={{ marginLeft: 10 }} disabled={loading} onClick={props.onAbort}>Avbryt</Button>
            </DialogActions>
        </Dialog>
    );
};

/* ------------------------------------------------------------------ */

type CopyYearDialogProps = {
    years: number[];
    defaultSource: number | null;
    onAbort: () => void;
    onCopied: (targetYear: number) => void;
};

const CopyYearDialog = (props: CopyYearDialogProps) => {
    const backend = new ApiBackend();
    const [sourceYear, setSourceYear] = useState<number>(props.defaultSource ?? (props.years.length ? props.years[props.years.length - 1] : new Date().getFullYear()));
    const [targetYear, setTargetYear] = useState<number>((props.defaultSource ?? new Date().getFullYear()) + 1);
    const [loading, setLoading] = useState(false);

    const handleCopy = async () => {
        if (sourceYear === targetYear) { alert("Käll- och målår måste skilja sig"); return; }
        if (props.years.indexOf(targetYear) >= 0 &&
            !window.confirm(`Målåret ${targetYear} finns redan. Befintliga rader behålls och nya läggs till. Fortsätt?`)) {
            return;
        }
        setLoading(true);
        const ok = await backend.copyCounterSetupYear(sourceYear, targetYear);
        setLoading(false);
        if (ok) { props.onCopied(targetYear); } else { alert("Kopieringen misslyckades"); }
    };

    return (
        <Dialog disableEscapeKeyDown fullWidth maxWidth="sm" open={true}>
            <DialogTitle>Kopiera counter setup från ett år</DialogTitle>
            <DialogContent dividers>
                <Grid container spacing={2} alignItems="center">
                    <Grid item xs={6}>
                        <Typography variant="body2">Från år (mall)</Typography>
                        <Select native fullWidth value={sourceYear} onChange={(e) => setSourceYear(+(e.target.value as string))}>
                            {props.years.map(y => <option key={y} value={y}>{y}</option>)}
                        </Select>
                    </Grid>
                    <Grid item xs={6}>
                        <TextField type="number" fullWidth label="Till år" InputLabelProps={{ shrink: true }}
                            value={targetYear} onChange={(e) => setTargetYear(+e.target.value)} />
                    </Grid>
                    <Grid item xs={12}>
                        <Typography variant="caption" color="textSecondary">
                            Alla rader för källåret kopieras till målåret. Årtalet i eventKey ersätts automatiskt (t.ex. CV30-{sourceYear} → CV30-{targetYear}).
                        </Typography>
                    </Grid>
                </Grid>
            </DialogContent>
            <DialogActions>
                <Button color="secondary" variant="contained" disabled={loading} onClick={handleCopy}>Kopiera</Button>
                <Button color="secondary" variant="contained" style={{ marginLeft: 10 }} disabled={loading} onClick={props.onAbort}>Avbryt</Button>
            </DialogActions>
        </Dialog>
    );
};

/* ------------------------------------------------------------------ */

const useStyles = ({ palette }: Theme) => createStyles({
    root: {},
    cardHeader: {
        background: palette.secondary.main,
        color: palette.secondary.contrastText,
        padding: 3
    }
});

const tableHeadStyles = ({ palette }: Theme) => createStyles({
    head: {
        background: palette.primary.main,
        color: palette.primary.contrastText,
    }
});

const StyledTableCell = withStyles(tableHeadStyles)(TableCell);

export default withStyles(useStyles)(ManageCounters);
